/**
 * Paraludum - Main Landing Page
 * Game selection hub
 */

import './styles/main.css';
import { initLanguage, getCurrentLanguage, setLanguage, t } from './lib/i18n';
import { VERSION } from './lib/version';

// Game catalog
interface Game {
  id: string;
  titleKey: string;
  descriptionKey: string;
  playersKey: string;
  emoji: string;
  path: string;
}

const games: Game[] = [
  {
    id: 'who-said-that',
    titleKey: 'landing.games.whoSaidThat.title',
    descriptionKey: 'landing.games.whoSaidThat.description',
    playersKey: 'landing.games.whoSaidThat.players',
    emoji: '🎭',
    path: '/games/who-said-that/',
  },
  {
    id: 'the-one',
    titleKey: 'landing.games.theOne.title',
    descriptionKey: 'landing.games.theOne.description',
    playersKey: 'landing.games.theOne.players',
    emoji: '🎯',
    path: '/games/the-one/',
  },
  {
    id: 'two-truths-one-lie',
    titleKey: 'landing.games.twoTruths.title',
    descriptionKey: 'landing.games.twoTruths.description',
    playersKey: 'landing.games.twoTruths.players',
    emoji: '🙃',
    path: '/games/two-truths-one-lie/',
  }
];

/**
 * Update header, footer, and UI text with current language
 */
function updateContent() {
  // Update page title (browser tab)
  document.title = `Paraludum - ${t('landing.subtitle')}`;

  // Update title
  const titleElement = document.getElementById('landing-title');
  if (titleElement) {
    titleElement.textContent = `🎪 ${t('landing.title')}`;
  }

  // Update subtitle
  const subtitleElement = document.getElementById('landing-subtitle');
  if (subtitleElement) {
    subtitleElement.textContent = t('landing.subtitle');
  }

  // Update credits
  const creditsElement = document.getElementById('credits-text');
  if (creditsElement) {
    creditsElement.innerHTML = t('landing.credits');
  }

  // Update language switcher tooltip and flag
  const languageSwitcher = document.getElementById('language-switcher');
  if (languageSwitcher) {
    languageSwitcher.title = t('landing.languageSwitch');
    // Show the flag of the language we can switch TO
    languageSwitcher.textContent = getCurrentLanguage() === 'en' ? '🇭🇺' : '🇺🇸';
  }

  // Update footer
  const footerElement = document.getElementById('footer-text');
  if (footerElement) {
    footerElement.innerHTML = `${t('footer.version', { version: VERSION })} · ${t('footer.copyright')} <a href="https://parabonum.com" target="_blank" rel="noopener noreferrer" style="color: var(--primary-color); text-decoration: none;">Parabonum</a>`;
  }

  // Re-render games with new language
  renderGames();
}

// Render games
function renderGames() {
  const container = document.getElementById('games-container');
  if (!container) return;

  container.innerHTML = games.map(game => `
    <a href="${game.path}" class="game-card" style="
      display: block;
      text-decoration: none;
      color: inherit;
      background: var(--card-bg);
      border-radius: 12px;
      padding: 2rem;
      border: 2px solid var(--border-color);
      transition: all 0.2s ease;
      cursor: pointer;
    ">
      <div style="display: flex; align-items: center; gap: 1.5rem;">
        <div style="font-size: 3rem;">${game.emoji}</div>
        <div style="flex: 1;">
          <h2 style="margin: 0 0 0.5rem 0; font-size: 1.5rem; color: var(--text-primary);">
            ${t(game.titleKey)}
          </h2>
          <p style="margin: 0 0 0.75rem 0; color: var(--text-secondary); font-size: 1rem;">
            ${t(game.descriptionKey)}
          </p>
          <div style="color: var(--primary-color); font-size: 0.875rem; font-weight: 500;">
            ${t(game.playersKey)}
          </div>
        </div>
        <div style="font-size: 1.5rem; color: var(--primary-color);">→</div>
      </div>
    </a>
  `).join('');

  // Add hover effects
  const cards = container.querySelectorAll('.game-card');
  cards.forEach(card => {
    card.addEventListener('mouseenter', () => {
      (card as HTMLElement).style.borderColor = 'var(--primary-color)';
      (card as HTMLElement).style.transform = 'translateY(-4px)';
      (card as HTMLElement).style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
    });
    card.addEventListener('mouseleave', () => {
      (card as HTMLElement).style.borderColor = 'var(--border-color)';
      (card as HTMLElement).style.transform = 'translateY(0)';
      (card as HTMLElement).style.boxShadow = 'none';
    });
  });
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  // Initialize language
  initLanguage();

  // Update content with current language
  updateContent();

  // Setup language switcher
  const languageSwitcher = document.getElementById('language-switcher');
  languageSwitcher?.addEventListener('click', () => {
    const newLang = getCurrentLanguage() === 'en' ? 'hu' : 'en';
    setLanguage(newLang);
    updateContent();
  });
});
